import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../SEO';
import { appConfig } from '../../config/appConfig';
import saasHeroVisual from '../../images/dashboard.png';
import retentionVisual from '../../images/Comp_4.gif';
import efficiencyVisual from '../../images/Comp_3.gif';

const faqs = [
  {
    question: 'What ARR multiple can my SaaS company command today?',
    answer:
      'Top-quartile SaaS platforms delivering 110%+ net revenue retention, 20%+ free cash flow margins, and efficient CAC payback often clear 7-12x ARR. Growth investors will stretch higher for mission-critical vertical SaaS with low churn, while mid-market strategics trend toward 4-7x ARR depending on integration synergies.',
  },
  {
    question: 'How should we present cohorts and usage data to buyers?',
    answer:
      'Investors want revenue cohorts that tie marketing spend to retention. We rebuild dashboards showing logo and revenue retention, expansion by cohort, feature-level adoption, and unit economics so buyers see durable net dollar retention and pathway to upsell.',
  },
  {
    question: 'Do SaaS buyers require founders to roll equity?',
    answer:
      'Most software investment banks negotiate a 10-30% rollover for alignment, but we calibrate rollover to founder goals. In majority recap scenarios we often structure partial secondary liquidity plus staged earn-outs tied to specific ARR milestones instead of vague growth targets.',
  },
  {
    question: 'How fast can AMIVA run a SaaS sell-side process?',
    answer:
      'We typically build the SaaS data room, rebuild metrics, and draft positioning in 4-6 weeks. Buyer outreach and LOI waves run for 6-8 weeks, and exclusivity through close takes another 6-8. Expect 18-20 weeks total with disciplined preparation.',
  },
];

const structuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': 'SaaS M&A Advisory',
    'name': 'SaaS & Software M&A Advisor',
    'provider': {
      '@type': 'Organization',
      'name': appConfig.site.organizationName,
      'url': appConfig.site.baseUrl,
    },
    'areaServed': ['United States', 'Canada', 'United Kingdom', 'European Union'],
    'audience': {
      '@type': 'Audience',
      'name': 'Founders and CEOs of SaaS and software businesses planning an exit',
    },
    'url': `${appConfig.site.baseUrl}/saas-mna-advisor`,
    'description':
      'Sell-side advisory for B2B SaaS founders. We optimize ARR quality, net revenue retention, and buyer orchestration to deliver premium software M&A outcomes.',
    'offers': {
      '@type': 'Offer',
      'price': '0',
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock',
      'url': `${appConfig.site.baseUrl}/valuation`,
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  },
];

const heroMetrics = [
  {
    label: 'Active SaaS Buyers',
    value: '180+',
    description: 'strategic acquirers and PE sponsors running live software mandates with AMIVA.',
  },
  {
    label: 'Median Multiple Lift',
    value: '1.8x',
    description: 'incremental premium captured after ARR, retention, and efficiency rebuilds.',
  },
  {
    label: 'Cohort Rebuild Speed',
    value: '14 days',
    description: 'time to produce investor-grade cohorts and usage dashboards from raw exports.',
  },
];

const valuationPillars = [
  {
    title: 'ARR Quality & Momentum',
    points: [
      'Booked ARR growth rate segmented by new, expansion, and contraction.',
      'Gross revenue retention versus net revenue retention with cohort overlays.',
      'ACV distribution and wins by ICP, including vertical concentration controls.',
      'Pipeline conversion velocity, cycle length, and sales efficiency dashboards.',
    ],
  },
  {
    title: 'Unit Economics & Efficiency',
    points: [
      'Customer acquisition cost by channel with blended CAC payback in months.',
      'Contribution margin after implementation and customer success expense.',
      'Magic number and LTV:CAC ratios normalized for latest quarter performance.',
      'Cash burn multiples, gross margin trends, and capital efficiency scorecards.',
    ],
  },
  {
    title: 'Product Usage & Stickiness',
    points: [
      'Feature-level adoption highlighting mission-critical workflows.',
      'Daily active to monthly active ratios and expansion triggers.',
      'Depth of integrations and APIs that lock customers into the platform.',
      'Support ticket deflection, NPS, and qualitative proof of love metrics.',
    ],
  },
];

const prepChecklist = [
  'Reconcile ARR waterfall with GAAP revenue and deferred revenue schedules.',
  'Refresh customer cohorts with logo and revenue retention plus expansion pathways.',
  'Document product roadmap, release velocity, and technical debt mitigation plans.',
  'Compile security posture: SOC2, ISO, pen tests, data residency, incident response.',
  'Outline pricing strategy, discount governance, and planned monetization levers.',
  'Build hiring plan, key person risk matrix, and retention strategy for engineers.',
  'Organize legal docs: MSA templates, IP assignments, third-party licenses.',
  'Produce sales efficiency memo covering partner motion, channel mix, and PLG loops.',
];

const dealStructures = [
  {
    label: 'Majority Recap with PE Sponsor',
    description:
      'Anchor liquidity event for founders while rolling 20-30% into the platform for a second bite at exit. Ideal for ARR $5M-$25M with 30%+ YoY growth and path to $50M ARR.',
  },
  {
    label: 'Strategic Acquisition',
    description:
      'Full sale to an enterprise or category leader looking to expand product suite or vertical reach. Emphasis on integration readiness, cross-sell potential, and roadmap alignment.',
  },
  {
    label: 'Minority Growth Equity',
    description:
      'Raise non-control capital at premium valuation to accelerate GTM, M&amp;A, or product expansion while preserving founder control. Often includes structured liquidity for early investors.',
  },
  {
    label: 'Sell-Side Process for Dual-Track IPO',
    description:
      'Position the business for either strategic/financial sale or near-term IPO. We assemble banker-level S-1 narratives, KPI visibility, and governance upgrades to maximize optionality.',
  },
];

const listItemClasses =
  'flex items-start gap-3 text-sm sm:text-base text-gray-700 leading-relaxed';
const listDotClasses =
  'mt-1.5 h-2 w-2 flex-none rounded-full bg-amiva-primary';

const visualShowcase = [
  {
    image: saasHeroVisual,
    alt: 'SaaS revenue dashboard with ARR and retention metrics.',
    label: 'ARR Waterfall',
    metric: '118% NRR spotlight',
    caption: 'Board-ready waterfall visuals show expansion, contraction, and net retention in one slide.',
    gradient: 'from-[#0B192E]/70 via-[#2563EB]/60 to-[#38BDF8]/60',
  },
  {
    image: retentionVisual,
    alt: 'Investor deck slide highlighting cohort adoption.',
    label: 'Cohort Pulse',
    metric: '14 day rebuild',
    caption: 'Cohort rebuilds surface logo, revenue, and product adoption without spreadsheet fatigue.',
    gradient: 'from-[#12274A]/70 via-[#1B3A70]/60 to-[#9333EA]/60',
  },
  {
    image: efficiencyVisual,
    alt: 'Capital efficiency report tracking magic number.',
    label: 'Efficiency Lens',
    metric: '1.8x premium',
    caption: 'Magic number, burn multiple, and CAC payback benchmarked against buyer thresholds.',
    gradient: 'from-[#0F172A]/70 via-[#1E3A8A]/60 to-[#10B981]/60',
  },
];

const highlightStats = [
  {
    label: 'Buyer Conversion to Deep Diligence',
    value: '92%',
    detail: 'of invited buyers request technical and product diligence after reviewing our dashboards.',
  },
  {
    label: 'Cash at Close',
    value: '70%',
    detail: 'median upfront consideration founders secure across recent SaaS transactions.',
  },
  {
    label: 'Process Timeline',
    value: '18-20 weeks',
    detail: 'kickoff-to-close runways covering metric rebuilds, buyer waves, and exclusivity.',
  },
];

const productSignals = [
  {
    title: 'ARR Quality Scorecard',
    summary: 'Waterfalls, cohorts, and NRR visuals fit on a single investor slide.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h11M4 18h16" />
      </svg>
    ),
  },
  {
    title: 'Usage Pulse Layer',
    summary: 'Feature heatmaps and DAU/MAU trendlines spotlight stickiness immediately.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
      </svg>
    ),
  },
  {
    title: 'Capital Efficiency Pulse',
    summary: 'Magic number, burn multiple, and CAC payback benchmarked to buyer thresholds.',
    icon: (
      <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h4l2-4 4 8 2-4h4" />
      </svg>
    ),
  },
];

const processHighlights = [
  {
    title: 'Narrative Sprint',
    timeline: 'Weeks 1-2',
    items: ['Rebuild ARR waterfalls', 'Design investor storyboard', 'Prep security & compliance vault'],
  },
  {
    title: 'Market Activation',
    timeline: 'Weeks 3-6',
    items: ['Tiered outreach waves', 'Signal-based follow-ups', 'Weekly pipeline dashboards'],
  },
  {
    title: 'Deal Structuring',
    timeline: 'Weeks 7-10',
    items: ['Model cash vs. rollover scenarios', 'Align earn-outs to ARR milestones', 'Coordinate cross-functional diligence'],
  },
];

const buyerSignals = [
  'Strategic cloud platforms seeking category expansion',
  'PE platforms rolling up mission-critical vertical SaaS',
  'Legacy vendors accelerating cloud migration via acquisition',
  'International entrants pursuing North American footholds',
  'AI / analytics challengers needing workflow depth',
];

const personaProfiles = [
  {
    title: 'Vertical SaaS Operators',
    stat: '$5M–$30M ARR',
    description:
      'Mission-critical workflow platforms balancing growth and durability who need the right sponsor or strategic fit without derailing roadmap velocity.',
  },
  {
    title: 'Usage-Based Infrastructure & DevTools',
    stat: '120%+ NRR',
    description:
      'API-first or developer tooling companies where telemetry, uptime, and references drive premium strategic bids and optionality on secondary liquidity.',
  },
  {
    title: 'Legacy Platforms Migrating to SaaS',
    stat: 'Hybrid delivery',
    description:
      'Established vendors modernizing from on-prem to SaaS who must translate ARR quality, churn mitigation, and migration runway into investor conviction.',
  },
];

const relatedPlaybooks = [
  {
    title: 'Cross-Border M&A Advisory',
    description: 'Coordinate US and European buyers when your platform already has global ambition.',
    to: '/cross-border-mna-us-europe',
  },
  {
    title: 'Manufacturing & Precision Machining M&A',
    description: 'Showcase Industry 4.0 deployments that pair with your software to deliver full-stack value.',
    to: '/manufacturing-precision-machining-mna',
  },
  {
    title: 'HVAC & Home Services M&A Advisor',
    description: 'Service platforms adopting field software and membership analytics for recurring revenue.',
    to: '/hvac-home-services-mna-advisor',
  },
];

export default function SaaSAdvisor() {
  return (
    <div className="bg-white">
      <SEOHead
        title="SaaS & Software M&A Advisor | AMIVA Equity Partners"
        description="Sell-side advisory for B2B SaaS. AMIVA optimizes ARR quality, net revenue retention, GTM efficiency, and orchestrates premium buyer processes for software founders."
        keywords={[
          'SaaS M&A advisor',
          'software investment bank',
          'sell my SaaS company',
          'SaaS valuation multiples',
          'software M&A process',
        ]}
        canonical="/saas-mna-advisor"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Industries', path: '/industries' },
          { name: 'SaaS M&A Advisor', path: '/saas-mna-advisor' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B192E] via-[#12274A] to-[#1B3A70] text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-24 -left-28 w-[28rem] h-[28rem] bg-amiva-primary/45 rounded-full blur-3xl"></div>
          <div className="absolute bottom-[-6rem] right-[-5rem] w-[32rem] h-[32rem] bg-amiva-accent/35 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,_3fr)_minmax(0,_2.4fr)] gap-12 items-center">
            <div className="max-w-3xl">
              <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-6">
                SaaS &amp; Software M&amp;A Advisor
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
                Package ARR quality into an investor-grade narrative
              </h1>
              <p className="text-base sm:text-lg text-gray-200 leading-relaxed mb-6">
                We rebuild data rooms so buyers see net revenue retention, capital efficiency, and
                product stickiness at a glance. The result: faster diligence, better multiples, and
                founders who stay focused on shipping product instead of writing memos.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                {heroMetrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-white/15 bg-white/5 p-4 text-center"
                  >
                    <div className="text-2xl font-semibold text-white">{metric.value}</div>
                    <p className="mt-1 text-xs uppercase tracking-[0.28em] text-white/70">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                      {metric.description}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <Link to="/valuation" className="btn-primary px-8 py-4 text-lg font-semibold">
                  Benchmark My SaaS Valuation
                </Link>
                <a
                  href={appConfig.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center text-base font-semibold text-white hover:text-amiva-accent transition-colors"
                >
                  Talk with a software banker →
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-12 right-0 h-24 w-24 rounded-full bg-amiva-accent/40 blur-xl"></div>
              <div className="absolute bottom-[-2rem] -left-10 h-20 w-20 rounded-full bg-white/20 blur-lg"></div>
              <div className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/5 shadow-[0_30px_60px_rgba(11,25,46,0.45)]">
                <img
                  src={saasHeroVisual}
                  alt="Software metrics dashboard showing ARR and retention curves."
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-[#12274A]/35 via-[#1B3A70]/10 to-transparent"></div>
                <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1 text-xs uppercase tracking-[0.32em] text-white">
                  Investor-ready dashboards
                </div>
                <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur">
                  <p className="text-sm font-semibold text-white">
                    140% NRR cohort • <span className="text-white/80">burn multiple 0.8x</span>
                  </p>
                  <p className="mt-1 text-xs text-white/70">
                    Automated data pulls feed LOI prep so buyer analysts spend time modeling upside.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Profiles */}
      <section className="py-16 lg:py-20 bg-gray-50" id="founder-profiles">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Founder Snapshot
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              SaaS founder archetypes we guide to premium outcomes
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Your metrics story, secondary objectives, and product roadmap priorities shape the
              process. We build a calibrated buyer narrative for each profile below so diligence
              accelerates instead of distracting your teams.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {personaProfiles.map((profile) => (
              <div
                key={profile.title}
                className="rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-4 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  {profile.stat}
                </div>
                <h3 className="text-lg font-semibold text-gray-900">{profile.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{profile.description}</p>
                <div className="text-xs text-gray-500">
                  We tailor rollover, earn-out mechanics, and integration runway per founder goals.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Story Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              SaaS Visuals
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Cohorts, usage, and efficiency packaged for quick consumption
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Buyers want to see cohorts, usage, and capital efficiency without cross-referencing
              spreadsheets. These motion graphics and dashboards slot directly into diligence syncs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visualShowcase.map((shot) => (
              <div
                key={shot.label}
                className="group relative overflow-hidden rounded-3xl border border-gray-100 bg-gray-900/5 shadow-lg"
              >
                <img
                  src={shot.image}
                  alt={shot.alt}
                  className="h-60 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${shot.gradient} opacity-80 mix-blend-multiply`}
                ></div>
                <div className="absolute inset-0 flex flex-col justify-between p-6">
                  <div className="inline-flex items-center gap-2 self-start rounded-full bg-white/15 px-3 py-1 text-xs uppercase tracking-[0.28em] text-white">
                    {shot.label}
                  </div>
                  <div className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                    <div className="text-sm font-semibold text-white">{shot.metric}</div>
                    <p className="mt-1 text-xs text-white/75 leading-relaxed">{shot.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Overview Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card-gradient p-8 md:p-12 rounded-3xl shadow-xl">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-10">
              <div>
                <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-2">
                  SaaS outcomes snapshot
                </p>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Benchmarks software founders leverage in diligence calls
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-xl">
                Every data room we launch is tuned to investor expectations—from ARR quality to
                churn telemetry—so diligence moves quickly and multiples stay elevated.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlightStats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 transition-transform duration-300 hover:-translate-y-1 flex flex-col gap-3 h-full"
                >
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amiva-primary">
                    {stat.label}
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              SaaS Metrics That Move Multiples
            </h2>
            <p className="text-base text-gray-600">
              Visual dashboards compress data into instant insight
            </p>
          </div>

          {/* Visual SaaS Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-12">
            {[
              { icon: '📈', label: 'ARR Growth Rate', value: 'Momentum', sublabel: 'YoY growth trajectory with cohort breakdown' },
              { icon: '🔄', label: 'Net Revenue Retention', value: 'Stickiness', sublabel: 'Expansion minus churn shows product value' },
              { icon: '💰', label: 'Average Contract Value', value: 'Deal Size', sublabel: 'Enterprise vs SMB mix impacts valuation' },
              { icon: '📊', label: 'Gross Margin', value: 'Unit Economics', sublabel: 'After hosting, support, and implementation costs' },
              { icon: '⚡', label: 'Magic Number', value: 'Efficiency', sublabel: 'Sales/marketing ROI: spend vs ARR growth' },
              { icon: '🎯', label: 'CAC Payback Period', value: 'Cash Cycle', sublabel: 'Months to recover customer acquisition cost' },
              { icon: '👥', label: 'Product Engagement', value: 'Usage Depth', sublabel: 'DAU/MAU ratio predicts retention risk' },
              { icon: '🔒', label: 'Logo Retention', value: 'Churn Risk', sublabel: 'Customer count stability year-over-year' },
              { icon: '📱', label: 'API Integration', value: 'Lock-in', sublabel: 'Deep integrations create switching costs' },
              { icon: '⭐', label: 'Customer Satisfaction', value: 'NPS Score', sublabel: 'Expansion and referral leading indicator' },
              { icon: '🚀', label: 'Sales Pipeline', value: 'Visibility', sublabel: 'Coverage ratio and velocity benchmarks' },
              { icon: '💎', label: 'LTV:CAC Ratio', value: 'Return Profile', sublabel: 'Customer lifetime value vs acquisition cost' }
            ].map((metric, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 p-5 hover:border-blue-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="text-4xl mb-3 text-center">{metric.icon}</div>
                <div className="text-sm font-bold text-gray-900 mb-2 text-center">{metric.label}</div>
                <div className="text-xs font-semibold text-amiva-primary uppercase tracking-wider mb-2 text-center">{metric.value}</div>
                <div className="text-xs text-gray-600 leading-snug">{metric.sublabel}</div>
              </div>
            ))}
          </div>

          {/* Visual Signal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {productSignals.map((signal) => (
              <div key={signal.title} className="group relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-3xl border-2 border-blue-200 p-8 hover:border-blue-400 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full opacity-20 -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
                <div className="relative">
                  <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-5 group-hover:scale-110 transition-transform duration-300">
                    {signal.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{signal.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{signal.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase mb-4">
                  Deal flow rhythm
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                  SaaS Deal Structures We Navigate Daily
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Choose your path—majority recap, strategic exit, or growth equity—backed by
                  visual models that clarify cash, rollover, and earn-out dynamics on day one.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {processHighlights.map((phase) => (
                  <div key={phase.title} className="card-premium p-6 border border-gray-100">
                    <div className="text-xs font-semibold uppercase tracking-[0.32em] text-amiva-primary mb-3">
                      {phase.timeline}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amiva-primary"></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="card-gradient p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="space-y-4">
                {dealStructures.map((structure) => (
                  <div key={structure.label} className="bg-white/95 border border-gray-100 rounded-2xl p-5">
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-[0.28em] mb-2">
                      {structure.label}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{structure.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-amiva-gradient-subtle text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
                Buyer radar
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Mapping the SaaS Buyer Landscape
              </h2>
              <div className="card-gradient p-6 border border-gray-100 shadow-xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary mb-2">
                      Signal tracking
                    </p>
                    <p className="text-sm text-gray-600">Scorecards capture cheque size, thesis fit, and integration playbooks.</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary mb-2">
                      Outreach cadence
                    </p>
                    <p className="text-sm text-gray-600">Tiered waves, anonymized profiles, and momentum reviews every Friday.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-premium p-8 shadow-xl transition-all duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Active Buyer Categories</h3>
              <ul className="space-y-3">
                {buyerSignals.map((signal) => (
                  <li key={signal} className={listItemClasses}>
                    <span className={listDotClasses}></span>
                    <span>{signal}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-500 mt-6">
                Each profile includes references, integration case studies, and post-close behavior so you engage only with decisive buyers.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Exit Prep: Visual Roadmap
            </h2>
            <p className="text-base text-gray-600">
              8 workstreams tracked in real-time
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { num: '1', icon: '📊', title: 'ARR Waterfall with GAAP Revenue Reconciliation', color: 'blue' },
              { num: '2', icon: '👥', title: 'Customer Cohorts: Logo & Revenue Retention + Expansion', color: 'purple' },
              { num: '3', icon: '📝', title: 'Product Roadmap, Release Velocity, Tech Debt Plans', color: 'green' },
              { num: '4', icon: '🔒', title: 'Security Posture: SOC2, ISO, Pen Tests, Data Residency', color: 'red' },
              { num: '5', icon: '💰', title: 'Pricing Strategy, Discount Governance, Monetization Levers', color: 'yellow' },
              { num: '6', icon: '👨‍💻', title: 'Hiring Plan, Key Person Risk, Engineering Retention', color: 'indigo' },
              { num: '7', icon: '⚖️', title: 'Legal Docs: MSA Templates, IP Assignments, Licenses', color: 'gray' },
              { num: '8', icon: '🎯', title: 'Sales Efficiency: Partner Motion, Channel Mix, PLG Loops', color: 'pink' }
            ].map((item) => (
              <div
                key={item.num}
                className={`relative bg-white rounded-2xl border-2 border-blue-200 p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className={`flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white text-sm font-bold`}>
                    {item.num}
                  </span>
                  <span className="text-3xl">{item.icon}</span>
                </div>
                <h3 className="text-xs font-semibold text-gray-900 leading-tight">{item.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">SaaS M&amp;A FAQs</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Software founders share similar concerns—multiples, timeline, data room depth, and
              founder commitments. These answers synthesize what to expect when you run a rigorous
              SaaS sell-side with AMIVA.
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div
                key={faq.question}
                className="card-premium p-6 md:p-8 transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-amiva-primary transition-colors">
                  {faq.question}
                </h3>
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Link */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-amiva-primary/10 text-amiva-primary text-xs sm:text-sm font-semibold tracking-[0.32em] uppercase">
              Related Playbooks
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-5">
              Explore adjacent AMIVA go-to-market coverage
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed">
              Many modern SaaS platforms intersect with services, industrial enablement, or global
              expansion. Dive into the playbooks below if your growth story spans multiple sectors.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPlaybooks.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-3xl bg-white border border-gray-100 shadow-lg p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-1 hover:border-amiva-primary/40"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amiva-primary">
                  Playbook
                </span>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-amiva-primary">
                  {item.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed flex-1">{item.description}</p>
                <span className="text-sm font-semibold text-amiva-primary group-hover:text-amiva-accent inline-flex items-center gap-2">
                  View details
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M3.5 10.5L10.5 3.5M10.5 3.5H4.375M10.5 3.5V9.625"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-amiva-primary to-amiva-accent text-white py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">Ready to explore the market?</h2>
            <p className="text-lg text-white/90">
              We will benchmark your SaaS KPIs, align on target structures, and orchestrate the
              buyer conversations that produce premium software exits.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/valuation" className="bg-white text-amiva-primary font-semibold px-6 py-3 rounded-xl text-center">
              Start the Valuation
            </Link>
            <a
              href={appConfig.links.calendly}
              target="_blank"
              rel="noreferrer"
              className="border border-white/70 rounded-xl px-6 py-3 font-semibold text-center hover:bg-white/10 transition"
            >
              Book a Founder Briefing
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
